import {inject, Injectable} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {EnvironmentService} from '../environment.service';
import {ConnectConnectionsService, ConnectOAuthAuthorizationRequest} from './connect-connections.service';

@Injectable({providedIn: 'root'})
export class ConnectOAuthPopupService {
  private readonly connections = inject(ConnectConnectionsService);
  private readonly environment = inject(EnvironmentService);

  authorize(request: ConnectOAuthAuthorizationRequest): Promise<void> {
    const auth = new URL(this.environment.authBaseServerUrl || window.location.origin);
    const bootstrap = new URL('/api/v1/auth/browser', auth);
    bootstrap.searchParams.set('popup_origin', window.location.origin);
    // This first-party page sets an HttpOnly browser nonce before authenticated HTTP issues state.
    const popup = window.open(bootstrap.toString(), 'ShineAuth', 'width=600,height=720,resizable=yes,scrollbars=yes');
    if (!popup) return Promise.reject(new Error('Allow popups to connect this account'));
    return new Promise<void>((resolve, reject) => {
      let settled = false;
      let started = false;
      const finish = (error?: Error) => {
        if (settled) return;
        settled = true;
        window.removeEventListener('message', onMessage);
        clearInterval(poll);
        clearTimeout(deadline);
        try { popup.close(); } catch {}
        if (error) reject(error); else resolve();
      };
      const onMessage = (event: MessageEvent) => {
        if (event.origin !== auth.origin || event.source !== popup || !event.data || typeof event.data !== 'object') return;
        if (event.data.type === 'auth:ready' && !started) {
          if (typeof event.data.nonce !== 'string' || !/^[a-zA-Z0-9_-]{43}$/.test(event.data.nonce)) return;
          started = true;
          void firstValueFrom(this.connections.authorize({...request, browser_nonce: event.data.nonce})).then(response => {
            if (settled) return;
            const target = new URL(response.url);
            if (target.protocol !== 'https:' || target.username || target.password) throw new Error('Invalid authentication destination');
            popup.location.href = target.toString();
            popup.focus();
          }).catch(() => finish(new Error('Unable to start authentication. Check your client permissions and try again.')));
        } else if (event.data.type === 'auth:callback' && started) {
          finish(event.data.ok === true ? undefined : new Error('Account authentication failed. Please try again.'));
        }
      };
      window.addEventListener('message', onMessage);
      const poll = setInterval(() => {
        if (popup.closed) finish(new Error('Authentication window was closed'));
      }, 300);
      const deadline = setTimeout(() => finish(new Error('Authentication timed out. Please try again.')), 120000);
    });
  }
}
